"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./S3VideoPlayer.module.scss";
import {
  VideoSource,
  VideoMetadata,
  VideoPlayerConfig,
  VideoPlayerEvents,
  VideoPlayerStateData,
  DeviceType,
} from "@interfaces/video";
import assetLoader from "@utils/internalImageLoder";
import {
  generateThumbnailUrl,
  formatTime,
  calculateProgress,
  isHLSStream,
  loadHLS,
  preloadHLS,
  startBufferStallMonitoring,
  getVideoFileExtension,
  supportsVideoFormat,
  recoverFromBufferStall,
} from "@utils/videoUtils";
import Image from "next/image";

interface S3VideoPlayerProps {
  video: VideoSource;
  metadata: VideoMetadata;
  config?: VideoPlayerConfig;
  events?: VideoPlayerEvents;
  deviceType: DeviceType;
  isVisible: boolean;
  className?: string;
}

const getVideoUrl = (url: string): string => {
  if (url.endsWith('.mp4')) {
    url = url.replace('.mp4', '.m3u8').replace('/default', '');
  }
  return url;
};

const S3VideoPlayer: React.FC<S3VideoPlayerProps> = ({
  video,
  metadata,
  config = {},
  events = {},
  deviceType,
  isVisible,
  className,
}) => {
  // Add retry tracking to prevent infinite loops
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const currentVideoUrlRef = useRef<string>('');
  video.url = getVideoUrl(video.url);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playerState, setPlayerState] = useState<VideoPlayerStateData>({
    currentTime: 0,
    duration: 0,
    progress: 0,
    volume: 1,
    isMuted: config.muted || false,
    isPlaying: false,
    isPaused: false,
    isEnded: false,
    isLoading: false,
    hasError: false,
  });

  const [showControls, setShowControls] = useState(true);
  const [showHoverControls, setShowHoverControls] = useState(false);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const defaultConfig: VideoPlayerConfig = {
    autoplay: true,
    muted: false,
    controls: false,
    loop: false,
    preload: "metadata",
    showTitle: true,
    showProgress: true,
    showVolume: true,
    showClose: true,
    ...config,
  };

  // Setup video event listeners
  const setupVideoEventListeners = useCallback(() => {
    if (!videoRef.current) return;

    const videoElement = videoRef.current;

    const handleLoadedMetadata = () => {
      setPlayerState((prev) => ({
        ...prev,
        duration: videoElement.duration,
        isLoading: false,
      }));
      events.onReady?.();

      // Autoplay if configured
      if (defaultConfig.autoplay) {
        videoElement.play().catch((error: any) => {
          console.warn("Autoplay was prevented:", error);
          setShowControls(true);
        });
      }
    };

    const handleTimeUpdate = () => {
      const currentTime = videoElement.currentTime;
      const duration = videoElement.duration;
      const progress = calculateProgress(currentTime, duration);

      setPlayerState((prev) => ({
        ...prev,
        currentTime,
        progress,
      }));

      events.onTimeUpdate?.(currentTime, duration);
      events.onProgress?.(progress);
    };

    const handlePlay = () => {
      setPlayerState((prev) => ({
        ...prev,
        isPlaying: true,
        isPaused: false,
        isEnded: false,
        isLoading: false,
        hasError: false,
      }));
      events.onPlay?.();
    };

    const handlePause = () => {
      setPlayerState((prev) => ({
        ...prev,
        isPlaying: false,
        isPaused: true,
      }));
      events.onPause?.();
    };

    const handleEnded = () => {
      setPlayerState((prev) => ({
        ...prev,
        isPlaying: false,
        isPaused: false,
        isEnded: true,
      }));
      events.onEnded?.();
    };

    const handleVolumeChange = () => {
      setPlayerState((prev) => ({
        ...prev,
        volume: videoElement.volume,
        isMuted: videoElement.muted,
      }));
      events.onVolumeChange?.(videoElement.volume, videoElement.muted);
    };

    const handleError = () => {
      const videoElement = videoRef.current;

      // Get detailed error information
      let errorDetails = "Unknown error";
      if (videoElement && videoElement.error) {
        const error = videoElement.error;
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorDetails = "Video loading was aborted";
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorDetails = "Network error while loading video";
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorDetails = "Video decoding error";
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorDetails = "Video format not supported";
            break;
          default:
            errorDetails = `Video error (code: ${error.code})`;
        }
      }

      // For HLS streams, ignore native video errors during HLS loading
      if (videoElement && isHLSStream(videoElement.src)) {
        const hls = (videoElement as any).hls;

        // If HLS is still loading or has been loaded, ignore native video errors
        if (hls || videoElement.readyState >= 2) {
          return;
        }

        // If HLS failed but native video is working, don't show error
        if (!hls && videoElement.readyState >= 2) {
          setPlayerState((prev) => ({
            ...prev,
            hasError: false,
            isLoading: false,
          }));
          return;
        }

        // If HLS is loading, don't show error yet
        if ((videoElement as any).hlsLoading) {
          return;
        }
      }

      setPlayerState((prev) => ({
        ...prev,
        hasError: true,
        isLoading: false,
      }));
      events.onError?.(errorDetails);
    };

    const handleLoadStart = () => {
      setPlayerState((prev) => ({ ...prev, isLoading: true }));

      // Add timeout to prevent infinite loading
      setTimeout(() => {
        setPlayerState((prev) => {
          if (prev.isLoading) {
            return { ...prev, isLoading: false };
          }
          return prev;
        });
      }, 10000); // 10 second timeout
    };

    const handleCanPlay = () => {
      setPlayerState((prev) => ({ ...prev, isLoading: false }));

      // Reset retry count on successful load
      retryCountRef.current = 0;

      // If video can play and we had an error, clear the error state
      // This handles the fallback scenario where HLS fails but native video works
      if (playerState.hasError) {
        setTimeout(() => {
          setPlayerState((prev) => ({
            ...prev,
            hasError: false,
          }));
        }, 1000); // Give it a moment to ensure video is actually working
      }
    };

    const handleStalled = () => {
      if (videoRef.current) {
        const hls = (videoRef.current as any).hls;
        recoverFromBufferStall(videoRef.current, hls);
      }
    };

    const handleWaiting = () => {
      if (videoRef.current) {
        const hls = (videoRef.current as any).hls;
        recoverFromBufferStall(videoRef.current, hls);
      }
    };

    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("pause", handlePause);
    videoElement.addEventListener("ended", handleEnded);
    videoElement.addEventListener("volumechange", handleVolumeChange);
    videoElement.addEventListener("error", handleError);
    videoElement.addEventListener("loadstart", handleLoadStart);
    videoElement.addEventListener("canplay", handleCanPlay);
    videoElement.addEventListener("stalled", handleStalled);
    videoElement.addEventListener("waiting", handleWaiting);

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("pause", handlePause);
      videoElement.removeEventListener("ended", handleEnded);
      videoElement.removeEventListener("volumechange", handleVolumeChange);
      videoElement.removeEventListener("error", handleError);
      videoElement.removeEventListener("loadstart", handleLoadStart);
      videoElement.removeEventListener("canplay", handleCanPlay);
      videoElement.removeEventListener("stalled", handleStalled);
      videoElement.removeEventListener("waiting", handleWaiting);
    };
  }, [events, defaultConfig.autoplay]);

  const handleRetry = () => {
    // Check retry limit
    if (retryCountRef.current >= maxRetries) {
      console.error("Max retry attempts reached");
      setPlayerState((prev) => ({
        ...prev,
        hasError: true,
        isLoading: false,
      }));
      return;
    }

    // Increment retry count
    retryCountRef.current += 1;

    setPlayerState((prev) => ({
      ...prev,
      hasError: false,
      isLoading: true,
    }));

    // Clean up existing HLS instance
    if (videoRef.current && (videoRef.current as any).hls) {
      try {
        (videoRef.current as any).hls.destroy();
        (videoRef.current as any).hls = null;
      } catch (error) {
        console.error("Error destroying HLS instance during retry:", error);
      }
    }

    // Reload the video
    if (videoRef.current) {
      videoRef.current.load();

      // Reinitialize HLS if needed
      if (isHLS) {
        loadHLS(videoRef.current, videoUrl, deviceType).catch((error) => {
          console.error("Failed to reinitialize HLS:", error);
          setPlayerState((prev) => ({
            ...prev,
            hasError: true,
            isLoading: false,
          }));
        });
      }
    }
  };

  const thumbnailUrl = generateThumbnailUrl(video, metadata.thumbnail);
  const videoUrl = getVideoUrl(video.url);

  // Validate video URL
  if (!videoUrl || videoUrl.trim() === '') {
    console.error("Invalid video URL:", videoUrl);
  }

  // Check if video is HLS
  const isHLS = isHLSStream(videoUrl);

  // Initialize video when visible
  useEffect(() => {
    if (!isVisible) return;
    return setupVideoEventListeners();
  }, [isVisible, setupVideoEventListeners]);

  // Preload HLS.js when component mounts to improve loading performance
  useEffect(() => {
    if (isHLS) {
      preloadHLS().catch((error) => {
        console.warn("Failed to preload HLS.js:", error);
      });
    }
  }, [isHLS]);

  // Initialize HLS for HLS streams in browsers that don't support it natively
  useEffect(() => {
    if (!isVisible || !videoRef.current || !isHLS) return;

    // Reset retry count when component becomes visible
    retryCountRef.current = 0;

    const videoElement = videoRef.current;
    let bufferStallCleanup: (() => void) | null = null;

    // Add a small delay to ensure video element is properly initialized

    // Check if video element is still valid
    if (!videoElement || !videoRef.current) {
      return;
    }

    // Validate video URL before loading
    if (!videoUrl || videoUrl.trim() === '') {
      setPlayerState((prev) => ({
        ...prev,
        hasError: true,
        isLoading: false,
      }));
      return;
    }

    // Check if format is supported
    const videoExtension = getVideoFileExtension(videoUrl);
    const formatSupported = supportsVideoFormat(videoExtension);

    // For HLS streams, we'll handle them with HLS.js even if not natively supported
    if (!formatSupported && !isHLS) {
      setPlayerState((prev) => ({
        ...prev,
        hasError: true,
        isLoading: false,
      }));
      return;
    }

    // For HLS streams, always use HLS.js (even if not natively supported)
    if (isHLS) {
      // Check if we're already initializing the same video URL
      if (currentVideoUrlRef.current === videoUrl && ((videoElement as any).hls || (videoElement as any).hlsLoading)) {
        return;
      }

      // Update current video URL ref
      currentVideoUrlRef.current = videoUrl;

      // Clean up any existing HLS instance before initializing new one
      if ((videoElement as any).hls) {
        try {
          if ((videoElement as any).hlsCleanup) {
            (videoElement as any).hlsCleanup();
          }
          (videoElement as any).hls.destroy();
          (videoElement as any).hls = null;
          (videoElement as any).hlsCleanup = null;
        } catch (error) {
          console.error("Error destroying existing HLS instance:", error);
        }
      }

      // Add a small delay to prevent race conditions in React Strict Mode
      setTimeout(() => {
        // Double-check that we should still initialize (component might have unmounted)
        if (!videoRef.current || !isVisible) {
          return;
        }

        // Don't set video src for HLS - let HLS.js handle it
        loadHLS(videoElement, videoUrl, deviceType).then(() => {
          // Reset retry count on successful HLS load
          retryCountRef.current = 0;

          // Clear any previous errors when HLS loads successfully
          setPlayerState((prev) => ({
            ...prev,
            hasError: false,
            isLoading: false,
          }));

          // Start buffer stall monitoring after HLS is loaded
          const hls = (videoElement as any).hls;
          bufferStallCleanup = startBufferStallMonitoring(
            videoElement,
            hls,
            () => {
              // You can add UI feedback here if needed
            }
          );
        }).catch((error) => {
          console.error("Failed to initialize HLS:", error);

          // Clear the current video URL ref on error
          currentVideoUrlRef.current = '';

          // Don't set error state immediately, let the native video fallback work
          setPlayerState((prev) => ({
            ...prev,
            isLoading: false,
          }));

          // Set up fallback detection
          const checkFallback = () => {
            if (videoElement.readyState >= 2) {
              setPlayerState((prev) => ({
                ...prev,
                hasError: false,
              }));
            } else {
              // If video still can't load after a delay, show error
              setTimeout(() => {
                if (videoElement.readyState < 2) {
                  setPlayerState((prev) => ({
                    ...prev,
                    hasError: true,
                  }));
                }
              }, 3000);
            }
          };

          // Check fallback after a short delay
          setTimeout(checkFallback, 1000);
        });
      }, 100); // Small delay to prevent race conditions
    } else {
      if (videoElement.src !== videoUrl) {
        videoElement.src = videoUrl;
      }
    }


    // Cleanup function
    return () => {
      // Clean up buffer stall monitoring
      if (bufferStallCleanup) {
        bufferStallCleanup();
      }

      // Clean up HLS instance if it exists
      if ((videoElement as any).hls) {
        try {
          // Call custom cleanup first
          if ((videoElement as any).hlsCleanup) {
            (videoElement as any).hlsCleanup();
          }
          (videoElement as any).hls.destroy();
          (videoElement as any).hls = null;
          (videoElement as any).hlsCleanup = null;
        } catch (error) {
          console.error("Error destroying HLS instance:", error);
        }
      }
    };
  }, [isVisible, isHLS, video.url]);

  // Cleanup effect to reset HLS state when component unmounts or video changes
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        // Reset HLS loading state
        (videoRef.current as any).hlsLoading = false;
        currentVideoUrlRef.current = '';
      }
    };
  }, [video.url]);

  // Auto-hide controls
  useEffect(() => {
    if (!playerState.isPlaying) return;

    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }

    hideControlsTimeoutRef.current = setTimeout(() => {
      if (playerState.isPlaying) {
        setShowHoverControls(false);
        if (deviceType === DeviceType.DESKTOP) {
          setShowControls(false);
        }
      }
    }, 3000);

    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [showControls, showHoverControls, playerState.isPlaying, deviceType]);

  // Control functions
  const handlePlay = () => {
    videoRef.current?.play();
  };

  const handlePause = () => {
    videoRef.current?.pause();
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
    events.onReplay?.();
  };

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * playerState.duration;

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    setShowHoverControls(true);
  };

  const handleMouseLeave = () => {
    if (deviceType === DeviceType.DESKTOP && playerState.isPlaying) {
      setShowHoverControls(false);
    }
  };

  const handleTouchStart = () => {
    setShowControls(true);
    setShowHoverControls(true);
  };

  return (
    <div
      className={`${styles.s3VideoPlayer} ${className || ""}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      data-device={deviceType}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className={styles.videoElement}
        src={getVideoUrl(video.url)}
        poster={thumbnailUrl}
        muted={defaultConfig.muted}
        autoPlay={defaultConfig.autoplay}
        preload={defaultConfig.preload}
        playsInline
        style={{ display: "block" }}
        {...(isHLS && { type: "application/x-mpegURL" })}
      />

      {/* Media Chrome Poster Image for HLS - only show when video is not playing */}
      {isHLS && !playerState.isPlaying && (
        <div className={styles.mediaPoster}>
          <Image
            src={thumbnailUrl}
            alt="Video Poster"
            layout="fill"
            objectFit="cover"
          />
        </div>
      )}

      {/* Top Gradient Overlay */}
      <div className={styles.topGradient} />

      {/* Main Control Button */}
      {(!playerState.isPlaying || showHoverControls) && (
        <div className={styles.mainControl}>
          {playerState.isEnded ? (
            <button
              className={styles.replayButton}
              onClick={handleReplay}
              aria-label="Replay video"
            >
              <Image
                src={`${assetLoader()}/icons/replay-icon.svg`}
                alt="Replay"
                width={24}
                loading="lazy"
                height={24}
              />
            </button>
          ) : playerState.isPlaying ? (
            <button
              className={styles.pauseButton}
              onClick={handlePause}
              aria-label="Pause video"
            >
              <Image
                src={`${assetLoader()}/icons/pause-icon-1.svg`}
                alt="Pause"
                loading="lazy"
                width={7}
                height={23}
              />
              <Image
                src={`${assetLoader()}/icons/pause-icon-2.svg`}
                alt="Pause"
                loading="lazy"
                width={7}
                height={23}
              />
            </button>
          ) : (
            <button
              className={styles.playButton}
              onClick={handlePlay}
              aria-label="Play video"
            >
              <Image
                src={`${assetLoader()}/icons/play.svg`}
                alt="Play"
                width={25}
                height={28}
                loading="lazy"
              />
            </button>
          )}
        </div>
      )}

      {/* Close Button - Always visible */}
      {defaultConfig.showClose && events.onClose && (
        <button
          className={styles.closeButton}
          onClick={events.onClose}
          aria-label="Close video"
        >
          <Image
            src={`${assetLoader()}/icons/close-icon.svg`}
            alt="Close"
            width={14}
            loading="lazy"
            height={14}
          />
        </button>
      )}

      {/* Bottom Controls */}
      {(showControls || deviceType === DeviceType.MOBILE) && (
        <div className={styles.bottomControls}>
          <div className={styles.controlsTop}>
            <div className={styles.timeDisplay}>
              <span className={styles.currentTime}>
                {formatTime(playerState.currentTime)}
              </span>
              <span className={styles.totalTime}>
                / {formatTime(playerState.duration)}
              </span>
            </div>

            {defaultConfig.showVolume && (
              <button
                className={styles.volumeButton}
                onClick={handleVolumeToggle}
                aria-label={playerState.isMuted ? "Unmute" : "Mute"}
              >
                {playerState.isMuted ? (
                  <Image
                    src={`${assetLoader()}/icons/volume-mute-icon.svg`}
                    alt="Volume Mute"
                    width={24}
                    loading="lazy"
                    height={24}
                  />
                ) : (
                  <Image
                    src={`${assetLoader()}/icons/volume-icon.svg`}
                    alt="Volume"
                    width={24}
                    loading="lazy"
                    height={24}
                  />
                )}
              </button>
            )}
          </div>

          {defaultConfig.showProgress && (
            <div className={styles.progressBar} onClick={handleProgressClick}>
              <div
                className={styles.progressFill}
                style={{ width: `${playerState.progress}%` }}
              />
            </div>
          )}

          {defaultConfig.showTitle && (
            <div className={styles.videoTitle}>{metadata.title}</div>
          )}
        </div>
      )}

      {/* Loading State */}
      {playerState.isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
        </div>
      )}

      {/* Error State */}
      {playerState.hasError && (
        <div className={styles.errorOverlay}>
          <p>Failed to load video</p>
          <button onClick={handleRetry}>Retry</button>
        </div>
      )}


    </div>
  );
};

export default S3VideoPlayer;
